import { FunctionComponent, ReactNode, useContext, useEffect, useState, useMemo } from 'react'
import { IAnalyticReportWithFilters, IPostAnalyticReport } from '../../../../shared/services/analytic-report/analytic-report.model'
import { DialogSaveButtons } from '../../miscellaneous-components/dialog-save-buttons'
import { Box, Checkbox, Chip, FormControl, FormControlLabel, FormHelperText, InputLabel, ListItemText, MenuItem, OutlinedInput, Radio, RadioGroup, Select, SelectChangeEvent, Stack, Typography, useMediaQuery } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import moment, { Moment } from 'moment'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { ICountry, ILocation } from '../../../../shared/services/location/location.model'
import { IProject } from '../../../../shared/services/project/project.model'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import { IGroup } from '../../../../shared/services/group/group.model'
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category'
import { ICategory } from '../../../../shared/services/category/category.model'
import GroupService from '../../../../shared/services/group/group.service'
import CategoryService from '../../../../shared/services/category/category.service'
import LocationService from '../../../../shared/services/location/location.service'
import ProjectService from '../../../../shared/services/project/project.service'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import ContactSupplierService from '../../../../shared/services/contact-supplier/contact-supplier.service'
import { IContactSupplierDetails } from '../../../../shared/services/contact-supplier/contact-supplier.model'
import { SupplierPicker } from '../../form-field-components/supplier-picker'
import gaiaTheme from '../../../../root-components/theme'
import DataTypeService from '../../../../shared/services/data-types/data-types.service'
import { IDataTypeRelation } from '../../../../shared/services/data-types/data-types.model'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { getDisplayLabel } from '../../../../shared/utils/screen-labels'
import { getCategoryDisplayName, getCategoryGroups, sortCategoriesByDisplayName } from '../../../../shared/utils/category-utils';

// The default filter set. Also drives the three chart-dimension dropdowns, so narrowing it
// narrows all four places at once.
export const ALL_REPORT_FILTERS = [
  'Scope',
  'Group',
  'Category',
  'Category Group',
  'Country',
  'Location',
  'Subsidiary',
  'Project',
  'Supplier',
  'Data Type'
]

// Location / Subsidiary / Project / Supplier reference rows that exist only inside one
// organisation, so a partner's selections would be meaningless in a client account. The
// partner Library passes this set instead.
export const PORTABLE_REPORT_FILTERS = [
  'Scope',
  'Group',
  'Category',
  'Category Group',
  'Country',
  'Data Type'
]

export const AnalyticsReportForm: FunctionComponent<{
  onSubmit: (values: IPostAnalyticReport, resetForm: () => void) => void,
  isEditPage: boolean,
  isSubmitting: boolean,
  report?: IAnalyticReportWithFilters,
  // Which filters this form offers. Defaults to all of them; the partner Library passes
  // PORTABLE_REPORT_FILTERS so the org-scoped ones are hidden and never fetched.
  availableFilters?: string[],
  // 'id' picks one of this organisation's report periods. 'name' picks a period NAME, which
  // the Library resolves against each client's own periods at push time.
  periodMode?: 'id' | 'name',
  periodNameOptions?: { name: string, clientCount: number }[],
  periodClientCount?: number,
  // Where Cancel goes back to.
  cancelTo?: string,
  // Rendered under the filter picker; the Library explains the missing filters here.
  filterNote?: ReactNode,
}> = ({
  onSubmit,
  isEditPage,
  isSubmitting,
  report,
  availableFilters = ALL_REPORT_FILTERS,
  periodMode = 'id',
  periodNameOptions = [],
  periodClientCount = 0,
  cancelTo,
  filterNote,
}) => {
    const navigate = useNavigate()
    //Services
    const categoryService = new CategoryService()
    const groupService = GroupService.getInstance();
    const locationService = LocationService.getInstance();
    const projectService = ProjectService.getInstance()
    const subsidiaryService = SubsidiaryService.getInstance()
    const dataTypeService = new DataTypeService()
    const contactSupplierService = ContactSupplierService.getInstance()
    //Context
    const userDetailsContext = useContext(UserDetailsContext)
    const reportPeriodContext = useContext(ReportPeriodContext)
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [groups, setGroups] = useState<IGroup[]>()
    const [dataTypes, setDataTypes] = useState<IDataTypeRelation[]>()
    const [contactSuppliers, setContactSuppliers] = useState<IContactSupplierDetails[]>()
    const [categories, setCategories] = useState<ICategory[]>()
    const [locations, setLocations] = useState<ILocation[]>()
    const [countries, setCountries] = useState<ICountry[]>()
    const [projects, setProjects] = useState<IProject[]>()
    const [subsidiaires, setSubsidiaires] = useState<ISubsidiary[]>()
    const [openStartDate, setOpenStartDate] = useState(false);
    const [openEndDate, setOpenEndDate] = useState(false);
    //Layout
    const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
    //Variables
    const enabledProjects = userDetailsContext.userDetails?.organisation.enableProjects
    const enabledSubsidiaries = userDetailsContext.userDetails?.organisation.enableSubsidiaries
    const filters = availableFilters
    // The category groups on offer, read off the categories already loaded for the Category filter.
    const categoryGroups = useMemo(() => getCategoryGroups(categories), [categories])
    const scopes = [
      '1',
      '2',
      '3'
    ]
    //Use Effects
    useEffect(() => {
      dataTypeService.getAll()
        .then(response => {
          setDataTypes(response)
        })
        .catch(err => console.log(err))

      groupService.getTaggedGroups()
        .then(response => {
          setGroups(response)
        })
        .catch(err => console.log(err))

      categoryService.getAllCategories()
        .then(response => {
          setCategories(response)
        })
        .catch(err => console.log(err))

      // The four org-scoped lookups below are skipped when their filter is not offered. In
      // the partner Library that matters twice over: the partner org has no locations,
      // suppliers, projects or subsidiaries to return, and enabledProjects/enabledSubsidiaries
      // read the PARTNER's feature flags, which say nothing about any client.
      if (filters.includes('Supplier')) {
        contactSupplierService.getActiveSuppliersLite(false)
          .then(response => {
            setContactSuppliers(response)
          })
          .catch(err => console.log(err))
      }

      if (enabledSubsidiaries && filters.includes('Subsidiary')) {
        subsidiaryService.getAll()
          .then(response => {
            setSubsidiaires(response)
          })
          .catch(err => console.log(err))
      }

      if (enabledProjects && filters.includes('Project')) {
        projectService.getAllActive()
          .then(response => {
            setProjects(response)
          })
          .catch(err => console.log(err))
      }

      if (filters.includes('Location')) {
        locationService.getActiveLocations()
          .then(response => {
            setLocations(response)
          })
          .catch(err => console.log(err))
      }

      locationService.getAllCountries()
        .then(response => {
          setCountries(response)
        })
        .catch(err => console.log(err))
    }, [])

    const analyticReportSchema = Yup.object().shape({
      name: Yup.string().required('Please give this report a name'),
      reportingMetric: Yup.string().required('Please choose your reporting metric'),
      usingCustomDates: Yup.boolean(),
      // Whichever period field this form is actually using is the one that's required. In
      // Library mode the period is chosen by name (reportPeriodId stays null by design), so
      // requiring the id there would make the form permanently unsubmittable.
      reportPeriodId: Yup.string().when('usingCustomDates', {
        is: (usingCustomDates: boolean) => usingCustomDates === false && periodMode === 'id',
        then: schema => schema.required('Report period is required'),
        otherwise: schema => schema.notRequired()
      }),
      reportPeriodName: Yup.string().when('usingCustomDates', {
        is: (usingCustomDates: boolean) => usingCustomDates === false && periodMode === 'name',
        then: schema => schema.required('Report period is required'),
        otherwise: schema => schema.notRequired()
      }),
      startDate: Yup.string().when('usingCustomDates', {
        is: true,
        then: schema => schema.required('Start date is required'),
        otherwise: schema => schema.notRequired()
      }),
      endDate: Yup.string().when('usingCustomDates', {
        is: true,
        then: schema => schema.required('End date is required'),
        otherwise: schema => schema.notRequired()
      }),
      selectedFilters: Yup.array()
        .of(Yup.string())
        .min(1, 'At least one filter must be selected'),
      filterByScope: Yup.boolean(),
      scopes: Yup.array()
        .of(Yup.string())
        .when('filterByScope', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one scope is required')
              .required('Scope is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterByGroup: Yup.boolean(),
      groups: Yup.array()
        .of(Yup.string())
        .when('filterByGroup', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one group is required')
              .required('Group is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterByCategoryGroup: Yup.boolean(),
      categoryGroups: Yup.array()
        .of(Yup.string())
        .when('filterByCategoryGroup', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one category group is required')
              .required('Category group is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterByCategory: Yup.boolean(),
      categories: Yup.array()
        .of(Yup.string())
        .when('filterByCategory', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one category is required')
              .required('Category is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterByLocation: Yup.boolean(),
      locations: Yup.array()
        .of(Yup.string())
        .when('filterByLocation', {
          is: true,
          then: schema =>
            schema
              .min(1, `At least one ${screenLabelsContext.getLabel('location', { plural: false }) || 'location'} is required`)
              .required(`${screenLabelsContext.getLabel('location', { plural: false, capitalize: 'first' }) || 'Location'} is required`),
          otherwise: schema => schema.notRequired(),
        }),
      filterByCountry: Yup.boolean(),
      countries: Yup.array()
        .of(Yup.string())
        .when('filterByCountry', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one country is required')
              .required('Country is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterByDataType: Yup.boolean(),
      dataTypes: Yup.array()
        .of(Yup.string())
        .when('filterByDataType', {
          is: true,
          then: schema =>
            schema
              .min(1, 'At least one data type is required')
              .required('Data type is required'),
          otherwise: schema => schema.notRequired(),
        }),
      filterBySubsidiary: Yup.boolean(),
      subsidiaries: Yup.array()
        .of(Yup.string())
        .when('filterBySubsidiary', {
          is: true,
          then: schema =>
            schema
              .min(1, `At least one ${screenLabelsContext.getLabel('subsidiary', { plural: false })} is required`)
              .required(`${screenLabelsContext.getLabel('subsidiary', { plural: false, capitalize: 'first' })} is required`),
          otherwise: schema => schema.notRequired(),
        }),
      fi: Yup.boolean(),
      projects: Yup.array()
        .of(Yup.string())
        .when('filterByProject', {
          is: true,
          then: schema =>
            schema
              .min(1, `At least one ${screenLabelsContext.getLabel('project', { plural: false })} is required`)
              .required(`${screenLabelsContext.getLabel('project', { plural: false, capitalize: 'first' })} is required`),
          otherwise: schema => schema.notRequired(),
        }),
      filterBySupplier: Yup.boolean(),
      suppliers: Yup.array()
        .of(Yup.string())
        .when('filterBySupplier', {
          is: true,
          then: schema =>
            schema
              .min(1, `At least one ${screenLabelsContext.getLabel('supplier', { plural: false })} is required`)
              .required(`${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} is required`),
          otherwise: schema => schema.notRequired(),
        }),
      includePieChart: Yup.boolean(),
      pieChartCategory: Yup.string()
        .when('includePieChart', {
          is: true,
          then: schema =>
            schema
              .required('You must select a category for the pie chart'),
          otherwise: schema => schema.notRequired(),
        }),
      includeBarChart: Yup.boolean(),
      barChartCategory: Yup.string()
        .when('includeBarChart', {
          is: true,
          then: schema =>
            schema
              .required('You must select a category for the bar chart'),
          otherwise: schema => schema.notRequired(),
        }),
      includeStackedChart: Yup.boolean(),
      stackedChartCategory: Yup.string()
        .when('includeStackedChart', {
          is: true,
          then: schema =>
            schema
              .required('You must select a category for the stacked chart'),
          otherwise: schema => schema.notRequired(),
        }),
    })

    const formik = useFormik<IPostAnalyticReport>({
      initialValues: {
        name: '',
        reportingMetric: 'emissions',
        usingCustomDates: false,
        // Default to the user's last selected reporting period (still editable below).
        // Library mode ('name') never uses reportPeriodId, so it stays null there.
        reportPeriodId: periodMode === 'id' ? (reportPeriodContext.lastSelectedReportId ?? null) : null,
        // Library mode only: the period is chosen by name and resolved per client at push
        // time. Left null in the ordinary client form, which uses reportPeriodId.
        reportPeriodName: null as string | null,
        startDate: null,
        endDate: null,
        selectedFilters: [],
        filterByScope: false,
        scopes: [],
        filterByGroup: false,
        groups: [],
        filterByCategory: false,
        categories: [],
        filterByCategoryGroup: false,
        categoryGroups: [] as string[],
        filterByLocation: false,
        locations: [],
        filterByCountry: false,
        countries: [],
        filterByDataType: false,
        dataTypes: [],
        filterBySubsidiary: false,
        subsidiaries: [],
        filterByProject: false,
        projects: [],
        filterBySupplier: false,
        suppliers: [],
        includePieChart: false,
        pieChartCategory: null,
        includeBarChart: false,
        barChartCategory: null,
        includeStackedChart: false,
        stackedChartCategory: null,
      },
      validationSchema: analyticReportSchema,
      onSubmit: (values) => {
        if (values.pieChartCategory === '') {
          values.pieChartCategory = null
        }
        if (values.barChartCategory === '') {
          values.barChartCategory = null
        }
        if (values.stackedChartCategory === '') {
          values.stackedChartCategory = null
        }
        onSubmit(values, formik.resetForm)
      }
    })

    useEffect(() => {
      if (isEditPage && report) {
        formik.setValues({
          name: report.name,
          reportingMetric: report.reportingMetric,
          usingCustomDates: report.usingCustomDates,
          reportPeriodId: report.filters.reportPeriodId,
          // Library mode carries the period as a name rather than an id, so it comes from the
          // report itself rather than out of the filter blob.
          reportPeriodName: report.reportPeriodName ?? null,
          startDate: report.filters.startDate,
          endDate: report.filters.endDate,
          selectedFilters: report.filters.selectedFilters,
          filterByScope: report.filters.filterByScope,
          scopes: report.filters.scopes,
          filterByGroup: report.filters.filterByGroup,
          groups: report.filters.groups,
          filterByCategory: report.filters.filterByCategory,
          categories: report.filters.categories,
          // Absent on reports saved before this filter existed.
          filterByCategoryGroup: report.filters.filterByCategoryGroup ?? false,
          categoryGroups: report.filters.categoryGroups ?? [],
          filterByLocation: report.filters.filterByLocation,
          locations: report.filters.locations,
          filterByCountry: report.filters.filterByCountry,
          countries: report.filters.countries,
          filterByDataType: report.filters.filterByDataType,
          dataTypes: report.filters.dataTypes,
          filterBySubsidiary: report.filters.filterBySubsidiary,
          subsidiaries: report.filters.subsidiaries,
          filterByProject: report.filters.filterByProject,
          projects: report.filters.projects,
          filterBySupplier: report.filters.filterBySupplier,
          suppliers: report.filters.suppliers,
          includePieChart: report.pieChartCategory ? true : false,
          pieChartCategory: report.pieChartCategory,
          includeBarChart: report.barChartCategory ? true : false,
          barChartCategory: report.barChartCategory,
          includeStackedChart: report.stackedChartCategory ? true : false,
          stackedChartCategory: report.stackedChartCategory,
        })
      }
    }, [])

    // If the report periods were still loading when the form mounted, fill the period
    // default in once they arrive, without overwriting a choice already made.
    useEffect(() => {
      if (isEditPage || periodMode !== 'id') return
      if (formik.values.reportPeriodId || !reportPeriodContext.lastSelectedReportId) return
      formik.setFieldValue('reportPeriodId', reportPeriodContext.lastSelectedReportId)
    }, [reportPeriodContext.lastSelectedReportId])

    const displayFilterLabel = (filter: string): string => getDisplayLabel(filter, screenLabelsContext.getLabel)

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setValues({ ...formik.values, [name]: date.format('YYYY-MM-DD') })
      }
    }

    const selectedFiltersHandler = (e: SelectChangeEvent<string[]>) => {
      const { value } = e.target;
      formik.setFieldValue('selectedFilters', typeof value === 'string' ? value.split(',') : value)
      if (value.includes('Scope')) {
        formik.setFieldValue('filterByScope', true)
      }
      if (value.includes('Group')) {
        formik.setFieldValue('filterByGroup', true)
      }
      if (value.includes('Category')) {
        formik.setFieldValue('filterByCategory', true)
      }
      if (value.includes('Category Group')) {
        formik.setFieldValue('filterByCategoryGroup', true)
      }
      if (value.includes('Location')) {
        formik.setFieldValue('filterByLocation', true)
      }
      if (value.includes('Country')) {
        formik.setFieldValue('filterByCountry', true)
      }
      if (value.includes('Data Type')) {
        formik.setFieldValue('filterByDataType', true)
      }
      if (value.includes('Subsidiary')) {
        formik.setFieldValue('filterBySubsidiary', true)
      }
      if (value.includes('Project')) {
        formik.setFieldValue('filterByProject', true)
      }
      if (value.includes('Supplier')) {
        formik.setFieldValue('filterBySupplier', true)
      }
      if (!value.includes('Scope')) {
        formik.setFieldValue('filterByScope', false)
        formik.setFieldValue('scopes', [])
      }
      if (!value.includes('Group')) {
        formik.setFieldValue('filterByGroup', false)
        formik.setFieldValue('groups', [])
      }
      if (!value.includes('Category')) {
        formik.setFieldValue('filterByCategory', false)
        formik.setFieldValue('categories', [])
      }
      if (!value.includes('Category Group')) {
        formik.setFieldValue('filterByCategoryGroup', false)
        formik.setFieldValue('categoryGroups', [])
      }
      if (!value.includes('Location')) {
        formik.setFieldValue('filterByLocation', false)
        formik.setFieldValue('locations', [])
      }
      if (!value.includes('Country')) {
        formik.setFieldValue('filterByCountry', false)
        formik.setFieldValue('countries', [])
      }
      if (!value.includes('Data Type')) {
        formik.setFieldValue('filterByDataType', false)
        formik.setFieldValue('dataTypes', [])
      }
      if (!value.includes('Subsidiary')) {
        formik.setFieldValue('filterBySubsidiary', false)
        formik.setFieldValue('subsidiaries', [])
      }
      if (!value.includes('Project')) {
        formik.setFieldValue('filterByProject', false)
        formik.setFieldValue('projects', [])
      }
      if (!value.includes('Supplier')) {
        formik.setFieldValue('filterBySupplier', false)
        formik.setFieldValue('suppliers', [])
      }
    }

    const handleIncludePieChart = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('includePieChart', e.target.checked);
      if (!e.target.checked) {
        formik.setFieldValue('pieChartCategory', '');
      }
    };

    const handleIncludeBarChart = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('includeBarChart', e.target.checked);
      if (!e.target.checked) {
        formik.setFieldValue('barChartCategory', '');
      }
    };

    const handleIncludeStackedChart = (e: React.ChangeEvent<HTMLInputElement>) => {
      formik.setFieldValue('includeStackedChart', e.target.checked);
      if (!e.target.checked) {
        formik.setFieldValue('stackedChartCategory', '');
      }
    };

    const cancelHandler = () => {
      formik.resetForm()
      if (cancelTo) {
        navigate(cancelTo)
      } else if (isEditPage && report) {
        navigate(`/analytics/reports/${report.id}`)
      } else {
        navigate('/analytics/reports')
      }
    }

    return (
      <Box component='form'>
        <Box sx={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'lightGrey.main', borderRadius: '10px', p: isMobileMode ? 2 : 6 }}>
          <FormControl fullWidth variant='outlined' >
            <InputLabel htmlFor='name'>Name</InputLabel>
            <OutlinedInput id='name'
              name='name'
              fullWidth
              label='Name'
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              sx={{
                color: 'text.secondary'
              }}></OutlinedInput>
            {formik.touched.name && formik.errors.name &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.name ? <div>{formik.errors.name as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='reportingMetric'>Reporting Metric</InputLabel>
            <Select id='reportingMetric' name='reportingMetric' label='Reporting Metric'
              value={formik.values.reportingMetric}
              onChange={formik.handleChange}
              disabled
              error={formik.touched.reportingMetric && Boolean(formik.errors.reportingMetric)}>
              <MenuItem value={'emissions'}>Emissions
              </MenuItem>
            </Select>
            {formik.touched.reportingMetric && formik.errors.reportingMetric &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.reportingMetric}</FormHelperText>
            }
          </FormControl>
          <Typography variant='subtitle1' sx={{ mt: 2 }}>Emission Data</Typography>
          <FormControl fullWidth variant='outlined'>
            <RadioGroup
              onChange={(e) => {
                formik.setFieldValue('usingCustomDates', e.target.value === 'true');
              }}
              value={formik.values.usingCustomDates}
              sx={{
                width: '100%',
                display: 'flex', flexDirection: 'row', color: 'text.secondary'
              }}>
              <FormControlLabel value={'false'} control={<Radio />} label='Report Period' />
              <FormControlLabel value={'true'} control={<Radio />} label='Custom Dates' />
            </RadioGroup>
          </FormControl>
          {formik.values.usingCustomDates === false && periodMode === 'id' &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='reportPeriodId' id='reportPeriodId-label'>Report Period</InputLabel>
              <Select id='reportPeriodId' name='reportPeriodId' label='Report Period' labelId='reportPeriodId-label'
                value={formik.values.reportPeriodId || ''}
                onChange={formik.handleChange}
                error={formik.touched.reportPeriodId && Boolean(formik.errors.reportPeriodId)}>
                {reportPeriodContext.allReportPeriods.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)).map(reportPeriod => (
                  <MenuItem key={reportPeriod.id} value={reportPeriod.id}>{reportPeriod.friendlyName}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.reportPeriodId && formik.errors.reportPeriodId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.reportPeriodId}</FormHelperText>
              }
            </FormControl>}
          {/* Library mode: pick a period NAME. Report periods are org-scoped, so what travels
              to a client is the name, matched against their own periods when the report is
              pushed. The client count is shown so a name only one client uses is obvious. */}
          {formik.values.usingCustomDates === false && periodMode === 'name' &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='reportPeriodName' id='reportPeriodName-label'>Report Period</InputLabel>
              <Select id='reportPeriodName' name='reportPeriodName' label='Report Period' labelId='reportPeriodName-label'
                value={formik.values.reportPeriodName || ''}
                onChange={formik.handleChange}
                error={formik.touched.reportPeriodName && Boolean(formik.errors.reportPeriodName)}>
                {periodNameOptions.map(option => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.name}
                    <Typography variant='caption' sx={{ ml: 1, color: 'text.secondary' }}>
                      {option.clientCount} of {periodClientCount} client{periodClientCount === 1 ? '' : 's'}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText sx={formik.touched.reportPeriodName && formik.errors.reportPeriodName ? { color: '#bf3333', marginLeft: '16px !important' } : undefined}>
                {formik.touched.reportPeriodName && formik.errors.reportPeriodName
                  ? formik.errors.reportPeriodName
                  : `Each client's copy uses their own period with this name. If they don't have one, their most recent period is used instead and you'll be told which.`}
              </FormHelperText>
            </FormControl>}
          {formik.values.usingCustomDates === true &&
            <Stack display='flex' direction='row' spacing={2} sx={{ mt: 2 }}>
              <FormControl fullWidth variant='outlined'>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={formik.values.startDate ? moment(formik.values.startDate) : null}
                    onChange={(date) => dateHandler(date, 'startDate')}
                    format='DD/MM/YYYY'
                    open={openStartDate}
                    label='Start Date'
                    sx={{ width: '100%' }}
                    onOpen={() => setOpenStartDate(true)}
                    onClose={() => setOpenStartDate(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenStartDate(true),
                        error: formik.touched.startDate && Boolean(formik.errors.startDate)
                      },
                    }}
                  />
                  {formik.touched.startDate && formik.errors.startDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.startDate}</FormHelperText>
                  }
                </LocalizationProvider>
              </FormControl>
              <FormControl fullWidth variant='outlined'>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DatePicker
                    value={formik.values.endDate ? moment(formik.values.endDate) : null}
                    open={openEndDate}
                    minDate={moment(formik.values.startDate)}
                    label='End Date'
                    sx={{ width: '100%' }}
                    onChange={(date) => dateHandler(date, 'endDate')}
                    format='DD/MM/YYYY'
                    onOpen={() => setOpenEndDate(true)}
                    onClose={() => setOpenEndDate(false)}
                    slotProps={{
                      textField: {
                        onClick: () => setOpenEndDate(true),
                        error: formik.touched.startDate && Boolean(formik.errors.startDate)
                      },
                    }}
                  />
                  {formik.touched.endDate && formik.errors.endDate &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.endDate}</FormHelperText>
                  }
                </LocalizationProvider>
              </FormControl>
            </Stack>
          }
          <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
            <InputLabel htmlFor='filters'>Filters</InputLabel>
            <Select id='filters' name='filters' label='Filters'
              value={formik.values.selectedFilters}
              onChange={selectedFiltersHandler}
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value, index) => (
                    <Chip key={value} label={displayFilterLabel(value)} />
                  ))}
                </Box>
              )}
              error={formik.touched.selectedFilters && Boolean(formik.errors.selectedFilters)}
              input={<OutlinedInput label="Filters" />}>
              {filters.filter(f => !(f === 'Project' && !enabledProjects)).filter(f => !(f === 'Subsidiary' && !enabledSubsidiaries)).sort((a, b) => a.localeCompare(b)).map((filter, index) => (
                <MenuItem key={index} value={filter}>
                  <Checkbox checked={formik.values.selectedFilters?.includes(filter)} />
                  <ListItemText primary={displayFilterLabel(filter)} />
                </MenuItem>
              ))}
            </Select>
            {formik.touched.selectedFilters && formik.errors.selectedFilters &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                {formik.errors.selectedFilters ? <div>{formik.errors.selectedFilters as string}</div> : null}
              </FormHelperText>
            }
          </FormControl>
          {filterNote}
          {formik.values.filterByCategory === true && categories &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='categories'>Categories</InputLabel>
              <Select id='categories' name='categories' label='categories'
                value={formik.values.categories}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = getCategoryDisplayName(categories.find(category => category.id === value))
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.categories && Boolean(formik.errors.categories)}
                input={<OutlinedInput label="Categories" />}
              >
                {sortCategoriesByDisplayName(categories).map((category, index) => (
                  <MenuItem key={index} value={category.id}>
                    <Checkbox checked={formik.values.categories?.includes(category.id)} />
                    <ListItemText primary={getCategoryDisplayName(category)} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.categories && formik.errors.categories &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.categories ? <div>{formik.errors.categories as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByCategoryGroup === true && categories &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='categoryGroups'>Category Groups</InputLabel>
              <Select id='categoryGroups' name='categoryGroups' label='categoryGroups'
                value={formik.values.categoryGroups}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => (
                      <Chip key={index} label={categoryGroups.find(group => group.id === value)?.name ?? ''} />
                    ))}
                  </Box>
                )}
                error={formik.touched.categoryGroups && Boolean(formik.errors.categoryGroups)}
                input={<OutlinedInput label="Category Groups" />}
              >
                {categoryGroups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    <Checkbox checked={formik.values.categoryGroups?.includes(group.id)} />
                    <ListItemText primary={group.name} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.categoryGroups && formik.errors.categoryGroups &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.categoryGroups ? <div>{formik.errors.categoryGroups as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByCountry === true && countries &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='countries'>Countries</InputLabel>
              <Select id='countries' name='countries' label='countries'
                value={formik.values.countries}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = countries.find(country => country.id === value)?.name
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.countries && Boolean(formik.errors.countries)}
                input={<OutlinedInput label="Countries" />}
              >
                {countries.sort((a, b) => a.name.localeCompare(b.name)).map((country, index) => (
                  <MenuItem key={index} value={country.id}>
                    <Checkbox checked={formik.values.countries?.includes(country.id)} />
                    <ListItemText primary={country.name} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.countries && formik.errors.countries &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.countries ? <div>{formik.errors.countries as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByDataType === true && dataTypes &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='dataTypes'>Data Types</InputLabel>
              <Select id='dataTypes' name='dataTypes' label='dataTypes'
                value={formik.values.dataTypes}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = dataTypes.find(dataType => dataType.id === value)?.dataType
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.dataTypes && Boolean(formik.errors.dataTypes)}
                input={<OutlinedInput label="Data Types" />}
              >
                {dataTypes.sort((a, b) => a.dataType.localeCompare(b.dataType)).map((dataType, index) => (
                  <MenuItem key={index} value={dataType.id}>
                    <Checkbox checked={formik.values.dataTypes?.includes(dataType.id)} />
                    <ListItemText primary={dataType.dataType} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.dataTypes && formik.errors.dataTypes &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.dataTypes ? <div>{formik.errors.dataTypes as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByGroup === true && groups &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='groups'>Groups</InputLabel>
              <Select id='groups' name='groups' label='groups'
                value={formik.values.groups}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = groups.find(group => group.id === value)?.title
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.groups && Boolean(formik.errors.groups)}
                input={<OutlinedInput label="Groups" />}
              >
                {sortGroupsByGhgCategory(groups).map((group, index) => (
                  <MenuItem key={index} value={group.id}>
                    <Checkbox checked={formik.values.groups?.includes(group.id)} />
                    <ListItemText primary={formatGroupLabel(group)} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.groups && formik.errors.groups &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.groups ? <div>{formik.errors.groups as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByLocation === true && locations &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='locations'>{screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' })}</InputLabel>
              <Select id='locations' name='locations' label={screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' })}
                value={formik.values.locations}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = locations.find(location => location.id === value)?.friendlyName
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.locations && Boolean(formik.errors.locations)}
                input={<OutlinedInput label={screenLabelsContext.getLabel('location', { plural: true, capitalize: 'first' })} />}
              >
                {locations.sort((a, b) => a.friendlyName.localeCompare(b.friendlyName)).map((location, index) => (
                  <MenuItem key={index} value={location.id}>
                    <Checkbox checked={formik.values.locations?.includes(location.id)} />
                    <ListItemText primary={location.friendlyName} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.locations && formik.errors.locations &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.locations ? <div>{formik.errors.locations as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByProject === true && projects &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='projects'>{screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' })}</InputLabel>
              <Select id='projects' name='projects' label={screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' })}
                value={formik.values.projects}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = projects.find(project => project.id === value)?.summary
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.projects && Boolean(formik.errors.projects)}
                input={<OutlinedInput label={screenLabelsContext.getLabel('project', { plural: true, capitalize: 'first' })} />}
              >
                {projects.sort((a, b) => a.summary.localeCompare(b.summary)).map((project, index) => (
                  <MenuItem key={index} value={project.id}>
                    <Checkbox checked={formik.values.projects?.includes(project.id)} />
                    <ListItemText primary={project.summary} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.projects && formik.errors.projects &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.projects ? <div>{formik.errors.projects as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterByScope === true &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='scopes'>Scopes</InputLabel>
              <Select id='scopes' name='scopes' label='Scopes'
                value={formik.values.scopes}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => (
                      <Chip key={value} label={value} />
                    ))}
                  </Box>
                )}
                error={formik.touched.scopes && Boolean(formik.errors.scopes)}
                input={<OutlinedInput label="Scopes" />}
              >
                {scopes.map((scope, index) => (
                  <MenuItem key={index} value={scope}>
                    <Checkbox checked={formik.values.scopes?.includes(scope)} />
                    <ListItemText primary={scope} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.scopes && formik.errors.scopes &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.scopes ? <div>{formik.errors.scopes as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterBySubsidiary === true && subsidiaires &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='subsidiaries'>{screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}</InputLabel>
              <Select id='subsidiaries' name='subsidiaries' label={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}
                value={formik.values.subsidiaries}
                onChange={formik.handleChange}
                multiple
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value, index) => {
                      const selectedName = subsidiaires.find(subsidiary => subsidiary.id === value)?.name
                      return (
                        <Chip key={index} label={selectedName} />
                      )
                    })}
                  </Box>
                )}
                error={formik.touched.subsidiaries && Boolean(formik.errors.subsidiaries)}
                input={<OutlinedInput label={screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })} />}
              >
                {subsidiaires.sort((a, b) => a.name.localeCompare(b.name)).map((subsidiary, index) => (
                  <MenuItem key={index} value={subsidiary.id}>
                    <Checkbox checked={formik.values.subsidiaries?.includes(subsidiary.id)} />
                    <ListItemText primary={subsidiary.name} />
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.subsidiaries && formik.errors.subsidiaries &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.subsidiaries ? <div>{formik.errors.subsidiaries as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          {formik.values.filterBySupplier === true && contactSuppliers &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <SupplierPicker
                multiple
                id='suppliers'
                suppliers={contactSuppliers}
                label={screenLabelsContext.getLabel('supplier', { plural: true, capitalize: 'first' })}
                value={formik.values.suppliers}
                onChange={(ids) => formik.setFieldValue('suppliers', ids)}
                error={formik.touched.suppliers && Boolean(formik.errors.suppliers)}
              />
              {formik.touched.suppliers && formik.errors.suppliers &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.suppliers ? <div>{formik.errors.suppliers as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          <Typography variant='subtitle1' sx={{ mt: 2 }}>Charts</Typography>
          <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <FormControlLabel
              control={
                <Checkbox
                  id='includePieChart'
                  name='includePieChart'
                  checked={formik.values.includePieChart}
                  onChange={handleIncludePieChart}
                />}
              label={<Typography variant='body1'>Include Pie Chart</Typography>}
            />
          </FormControl>
          {formik.values.includePieChart &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
              <InputLabel htmlFor='pieChartCategory'>Pie Chart Category</InputLabel>
              <Select id='pieChartCategory' name='pieChartCategory' label='Pie Chart Category'
                value={formik.values.pieChartCategory}
                onChange={formik.handleChange}
                error={formik.touched.pieChartCategory && Boolean(formik.errors.pieChartCategory)}>
                {filters.filter(f => !(f === 'Project' && !enabledProjects)).filter(f => !(f === 'Subsidiary' && !enabledSubsidiaries)).sort((a, b) => a.localeCompare(b)).map((filter, index) => (
                  <MenuItem key={index} value={filter}>
                    <Typography>{displayFilterLabel(filter)}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.pieChartCategory && formik.errors.pieChartCategory &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.pieChartCategory ? <div>{formik.errors.pieChartCategory as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  id='includeBarChart'
                  name='includeBarChart'
                  checked={formik.values.includeBarChart}
                  onChange={handleIncludeBarChart}
                />}
              label={<Typography variant='body1'>Include Bar Chart</Typography>}
            />
          </FormControl>
          {formik.values.includeBarChart &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
              <InputLabel htmlFor='barChartCategory'>Bar Chart Category</InputLabel>
              <Select id='barChartCategory' name='barChartCategory' label='Bar Chart Category'
                value={formik.values.barChartCategory}
                onChange={formik.handleChange}
                error={formik.touched.barChartCategory && Boolean(formik.errors.barChartCategory)}>
                {filters.filter(f => !(f === 'Project' && !enabledProjects)).filter(f => !(f === 'Subsidiary' && !enabledSubsidiaries)).sort((a, b) => a.localeCompare(b)).map((filter, index) => (
                  <MenuItem key={index} value={filter}>
                    <Typography>{displayFilterLabel(filter)}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.barChartCategory && formik.errors.barChartCategory &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.barChartCategory ? <div>{formik.errors.barChartCategory as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
          <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  id='includeStackedChart'
                  name='includeStackedChart'
                  checked={formik.values.includeStackedChart}
                  onChange={handleIncludeStackedChart}
                />}
              label={<Typography variant='body1'>Include Stacked Chart</Typography>}
            />
          </FormControl>
          {formik.values.includeStackedChart &&
            <FormControl fullWidth variant='outlined' sx={{ mt: 1 }}>
              <InputLabel htmlFor='stackedChartCategory'>Stacked Chart Category</InputLabel>
              <Select id='stackedChartCategory' name='stackedChartCategory' label='Stacked Chart Category'
                value={formik.values.stackedChartCategory}
                onChange={formik.handleChange}
                error={formik.touched.stackedChartCategory && Boolean(formik.errors.stackedChartCategory)}>
                {filters.filter(f => !(f === 'Project' && !enabledProjects)).filter(f => !(f === 'Subsidiary' && !enabledSubsidiaries)).sort((a, b) => a.localeCompare(b)).map((filter, index) => (
                  <MenuItem key={index} value={filter}>
                    <Typography>{displayFilterLabel(filter)}</Typography>
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.stackedChartCategory && formik.errors.stackedChartCategory &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                  {formik.errors.stackedChartCategory ? <div>{formik.errors.stackedChartCategory as string}</div> : null}
                </FormHelperText>
              }
            </FormControl>
          }
        </Box>
        <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
          {!isEditPage &&
            <DialogSaveButtons
              isForm={true}
              isSubmitting={isSubmitting}
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
            />
          }
          {isEditPage && report &&
            <Stack direction='row' spacing={2} alignItems='center' justifyContent='flex-end' sx={{ mt: 3 }}>
              <DialogSaveButtons
                isForm={true}
                isSubmitting={isSubmitting}
                cancelHandler={cancelHandler}
                submitHandler={formik.submitForm}
              />
            </Stack>}
        </Stack>
      </Box>
    )
  }